import { ENEMY } from '../config/config';
import type { World } from '../game/world';
import type { Renderer } from '../gfx/renderer';
import { groundAhead, moveBody } from '../world/physics';
import { Entity } from './entity';

/** Shared enemy behaviour: gravity, patrol, defeat animations. */
export abstract class Enemy extends Entity {
  readonly kind = 'enemy' as const;
  /** Set once defeated by a shell / orb / prism: tumbles off the screen. */
  tumbling = false;
  private tumbleTimer = 0;
  /** Squash animation timer (stomped enemies). */
  squash = 0;
  /** Walk speed magnitude. */
  speed: number = ENEMY.grubSpeed;
  /** Reverse when the ground ahead runs out. */
  turnAtEdge = false;

  constructor(x: number, y: number, w: number, h: number) {
    super(x, y, w, h);
    this.facing = -1;
  }

  /** Gravity + tile collision + patrol turning. */
  protected patrol(dt: number, world: World): void {
    this.vy = Math.min(this.vy + 900 * dt, 280);
    this.vx = this.facing * this.speed;
    moveBody(this, world.map, dt, world.solidBoxes());
    if (this.hitLeft) this.facing = 1;
    else if (this.hitRight) this.facing = -1;
    else if (this.turnAtEdge && this.onGround && !groundAhead(world.map, this, this.facing)) {
      this.facing *= -1;
    }
  }

  protected updateTumble(dt: number, world: World): boolean {
    if (!this.tumbling) return false;
    this.tumbleTimer += dt;
    this.vy = Math.min(this.vy + 900 * dt, 400);
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    if (this.tumbleTimer > 3 || this.y > world.map.pixelH + 48) this.alive = false;
    return true;
  }

  /** Flip over and fall away (shell hit, orb hit, prism touch, block bump). */
  defeatFlip(world: World, points: number, dir = 0): void {
    if (this.tumbling || this.dying) return;
    this.tumbling = true;
    this.dying = true;
    this.vx = dir * 60;
    this.vy = -220;
    world.addScore(points, this.centerX, this.y);
    world.particles.defeat(this.centerX, this.centerY, this.particleColor);
    world.sfx('kick');
  }

  protected abstract get particleColor(): string;

  /** Awake enemies that walk into each other turn around. */
  bumpInto(other: Enemy): void {
    if (this.dying || other.dying) return;
    const dir = this.centerX < other.centerX ? -1 : 1;
    this.facing = dir;
    other.facing = -dir;
  }

  protected drawTumbling(r: Renderer, name: string): void {
    r.blitFlipY(r.sprites.facing(name, this.facing > 0), this.x, this.y);
  }
}
